import {
  Box,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useSessionContext } from "../../../../contexts/SessionContext";
import { useEffect, useState } from "react";
import { PractitionerDashboard } from "./components/PractitionerDashboard/PractitionerDashboard";

import { ManagerDashboard } from "./components/ManagerDashboard/ManagerDashboard";
import { ManagerContextProvider } from "../../../../contexts/ManagerContext";
import { GetSchedule } from "../../../../services/fhir";
import { oids } from "../../../../configs/Settings";
import { ModalFirstAccess } from "../ModalFirstAccess";
import { usePractitionerRoleContext } from "../../../../contexts/PractitionerRoleContext";
import { useViewContext } from "../../../../contexts/ViewContext";
import {
  practitionerRefsSharingAssociationCnpj,
  scheduleTouchesAllowedPractitioners,
} from "@/utils/practitionerRoleAssociationScope";
import { AdminPanelSettings, Person } from "@mui/icons-material";
import { CreateAppointmentDialog } from "../../../../pages/schedulemanager/components/creatappointmentdialog";
import { usePatientOrganizationContext } from "@/contexts/PatientOrganizationContext";
import { PatientSearch } from "./components/SearchPatients/PatientSearch";
import { DashboardNotifications } from "./components/DashboardNotifications/DashboardNotifications";
import { NotificationCenterButton } from "./components/DashboardNotifications/NotificationCenterButton";
import { LargeContentBox } from "@/components/common";
import { FluxPayScheduleFixer } from "./FluxPayScheduleFixer";
import { CalendarIcon } from "@mui/x-date-pickers";
import { PageHeader } from "@/components/common/PageHeader";
import dayjs from "dayjs";
import BirthdayDashboardSection from "src-next/modules/crm-clinical/communications/components/BirthdayDashboardSection";

const formatDate = (date: dayjs.Dayjs | null) => {
  return date
    ? date.format("dddd, D [de] MMMM [de] YYYY")?.charAt(0)?.toUpperCase() +
        date.format("dddd, D [de] MMMM [de] YYYY")?.slice(1)
    : "";
};

export function ProfessionalDashboard() {
  const {
    inviteDialog,
    user,
    access,
    firstAccessDialog,
    setFirstAccessDialog,
  } = useSessionContext();
  const { viewType, setViewType } = useViewContext();

  const { practitioner, practitionerRole, userSpecialties } =
    usePractitionerRoleContext();
  const {
    patients,
    patientOrganizationLoading,
    refetchPatientOrganization,
    patientOrganizationRefreshing,
  } = usePatientOrganizationContext();
  const [loadingVerify, setLoadingVerify] = useState<boolean>(true);
  const [schedule, setSchedule] = useState<any>();
  const [openFirstAccess, setOpenFirstAcccess] = useState<boolean>(!schedule);
  const [createAppointmentDialogOpen, setCreateAppointmentDialogOpen] =
    useState<boolean>(false);

  const anotherPatient = practitioner?.find(
    (e: any) => !e?.resource?.id.includes(user.username),
  );
  const verifyOwner =
    access?.roles?.includes("manager") && access?.roles?.includes("owner");

  const handleClose = () => {
    setFirstAccessDialog(false);
    setOpenFirstAcccess(false);
  };

  async function fetchSchedules() {
    if (!access?.organizationId) return;

    setLoadingVerify(true);
    try {
      const isManager =
        access?.roles?.includes("manager") || access?.roles?.includes("owner");

      const params: any = {
        organization: `${oids.cnpj}-${access.organizationId}`,
      };

      if (!isManager) {
        params.actor = `Practitioner/${oids.cpf}-${user.username}`;
      }

      const scheduleResponse = await GetSchedule(params);

      let entries = scheduleResponse?.entry;
      const scopeDigits = access?.managerAssociationScopeCnpjDigits;
      const allowed = practitionerRefsSharingAssociationCnpj(
        practitionerRole,
        scopeDigits,
      );
      if (scopeDigits && Array.isArray(entries)) {
        if (allowed.size === 0) {
          entries = [];
        } else {
          entries = entries.filter((e: any) =>
            scheduleTouchesAllowedPractitioners(e?.resource, allowed),
          );
        }
      }

      setSchedule(entries);
    } catch (err) {
      console.error("Erro ao buscar agendamentos:", err);
    } finally {
      setLoadingVerify(false);
    }
  }

  useEffect(() => {
    fetchSchedules();
  }, [
    access?.organizationId,
    access?.roles,
    access?.managerAssociationScopeCnpjDigits,
    user.username,
    practitionerRole,
  ]);

  return (
    <>
      {inviteDialog || loadingVerify ? (
        <Stack width="100%" spacing={2}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              // bgcolor: theme.palette.primary.main,
              borderRadius: 2,
            }}
          >
            <Grid container spacing={2}>
              {/* Header Row */}
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {/* Data skeleton */}
                  <Skeleton
                    variant="text"
                    width="300px"
                    height={32}
                    // sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                  />

                  {/* Actions skeleton */}
                  <Stack direction="row" spacing={2}>
                    <Skeleton
                      variant="rectangular"
                      width={150}
                      height={36}
                      sx={{
                        // bgcolor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: 1,
                      }}
                    />
                  </Stack>
                </Stack>
              </Grid>

              {/* Search Bar Row */}
              <Grid item xs={12}>
                <Stack spacing={2}>
                  {/* Search input skeleton */}
                  <Stack direction="row" spacing={2}>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={48}
                      sx={{
                        // bgcolor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: 2,
                      }}
                    />
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Stack spacing={2}>
                  {/* Search input skeleton */}
                  <Stack direction="row" spacing={2}>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={400}
                      sx={{
                        // bgcolor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: 2,
                      }}
                    />
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Stack>
      ) : (
        <LargeContentBox>
          <Stack width="100%" spacing={2}>
            {/* Cabeçalho com data e tabs na mesma linha */}
            <Paper elevation={0} sx={{ borderRadius: 1, overflow: "hidden" }}>
              <Grid
                container
                spacing={2}
                sx={{ pb: 1.5, px: 2 }}
                alignItems="center"
              >
                {/* Data atual - à esquerda */}
                <Grid
                  item
                  xs={12}
                  sm={access?.roles?.includes("manager") ? 4 : 12}
                >
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{
                      textAlign: { xs: "center", sm: "left" },
                    }}
                  >
                    {formatDate(dayjs())}
                  </Typography>
                </Grid>

                {/* Tabs Profissional | Clínica (apenas para managers) - centralizadas */}
                {access?.roles?.includes("manager") ? (
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      <Tabs
                        value={viewType}
                        onChange={(_, newValue: "user" | "admin") => {
                          if (newValue !== null) {
                            setViewType(newValue);
                            localStorage.setItem("viewType", newValue);
                          }
                        }}
                        centered
                      >
                        <Tab
                          value="user"
                          label={
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Person sx={{ mr: 1 }} />
                              Visão Profissional
                            </Box>
                          }
                        />
                        <Tab
                          value="admin"
                          label={
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <AdminPanelSettings sx={{ mr: 1 }} />
                              Visão Clínica
                            </Box>
                          }
                        />
                      </Tabs>
                    </Box>
                  </Grid>
                ) : null}

                {/* Espaço vazio à direita para manter tabs centralizadas (apenas para managers) */}
                {access?.roles?.includes("manager") ? (
                  <Grid
                    item
                    xs={false}
                    sm={4}
                    sx={{ display: { xs: "none", sm: "block" } }}
                  />
                ) : null}
              </Grid>
            </Paper>

            <FluxPayScheduleFixer
              schedules={schedule}
              onFixComplete={() => fetchSchedules()}
            />

            <BirthdayDashboardSection />

            <Stack width="100%">
              <PatientSearch
                patients={patients || []}
                loading={patientOrganizationLoading}
                // Refresh forçado do cache: a lista atual fica na tela.
                onRefresh={() => refetchPatientOrganization()}
                refreshing={patientOrganizationRefreshing}
              />
            </Stack>

            <PractitionerDashboard />

            {/* Notificações em destaque no dashboard (mesma fonte do header) */}
            <DashboardNotifications />
          </Stack>

          {!schedule &&
            verifyOwner &&
            !anotherPatient &&
            !userSpecialties &&
            firstAccessDialog &&
            !loadingVerify && (
              <ModalFirstAccess
                open={openFirstAccess}
                handleClose={handleClose}
              />
            )}
        </LargeContentBox>
      )}

      {/* Create Appointment Dialog */}
      {createAppointmentDialogOpen && (
        <CreateAppointmentDialog
          isOpen={createAppointmentDialogOpen}
          setModalOpen={setCreateAppointmentDialogOpen}
          walkin={true}
          cpfT=""
        />
      )}
    </>
  );
}
